import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Save, Clock, Loader2, CalendarOff } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const SLOT_OPTIONS = [10, 15, 20, 30, 45, 60];

export default function Schedules() {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [activeDoctorId, setActiveDoctorId] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newLeave, setNewLeave] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/doctors");
        setDoctors(data);
        const initial = user?.role === "doctor"
          ? user.user_id
          : data[0]?.user_id || "";
        setActiveDoctorId(initial);
      } catch (e) {
        toast.error("Failed to load doctors");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!activeDoctorId) return;
    (async () => {
      try {
        const { data } = await api.get(`/schedules/${activeDoctorId}`);
        setSchedule(data);
      } catch (e) {
        toast.error("Failed to load schedule");
      }
    })();
  }, [activeDoctorId]);

  const canEdit = user?.role === "admin" || (user?.role === "doctor" && user?.user_id === activeDoctorId);

  const addBlock = (dayKey) => {
    setSchedule((s) => ({
      ...s,
      weekly_hours: {
        ...(s.weekly_hours || {}),
        [dayKey]: [...((s.weekly_hours || {})[dayKey] || []), { start: "09:00", end: "13:00" }],
      },
    }));
  };

  const removeBlock = (dayKey, idx) => {
    setSchedule((s) => ({
      ...s,
      weekly_hours: {
        ...s.weekly_hours,
        [dayKey]: s.weekly_hours[dayKey].filter((_, i) => i !== idx),
      },
    }));
  };

  const updateBlock = (dayKey, idx, field, value) => {
    setSchedule((s) => ({
      ...s,
      weekly_hours: {
        ...s.weekly_hours,
        [dayKey]: s.weekly_hours[dayKey].map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
      },
    }));
  };

  const addLeave = () => {
    if (!newLeave) return;
    if ((schedule.leaves || []).includes(newLeave)) {
      toast.error("Date already added");
      return;
    }
    setSchedule((s) => ({ ...s, leaves: [...(s.leaves || []), newLeave].sort() }));
    setNewLeave("");
  };

  const removeLeave = (d) => {
    setSchedule((s) => ({ ...s, leaves: (s.leaves || []).filter((x) => x !== d) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        slot_duration_minutes: schedule.slot_duration_minutes,
        weekly_hours: schedule.weekly_hours,
        leaves: schedule.leaves,
      };
      const { data } = await api.put(`/schedules/${activeDoctorId}`, payload);
      setSchedule(data);
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex justify-center" data-testid="schedules-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (doctors.length === 0) {
    return (
      <div className="p-16 text-center" data-testid="schedules-empty-state">
        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "Manrope" }}>No doctors yet</h2>
        <p className="text-slate-500 text-sm">Add doctors from the Team page to set up schedules.</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto fade-in" data-testid="schedules-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">Availability</div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Doctor schedules
          </h1>
          <p className="text-sm text-slate-500 mt-1">Weekly hours, slot duration, and leave dates per doctor.</p>
        </div>
        <Button onClick={save} disabled={!canEdit || saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-schedule-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </div>

      <Card className="border-slate-200 mb-6">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Doctor</Label>
            <Select value={activeDoctorId} onValueChange={setActiveDoctorId}>
              <SelectTrigger data-testid="schedule-doctor-select" className="mt-1.5">
                <SelectValue placeholder="Select doctor" />
              </SelectTrigger>
              <SelectContent>
                {doctors.map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name} · {d.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Slot duration (minutes)</Label>
            <Select
              value={String(schedule?.slot_duration_minutes || 30)}
              onValueChange={(v) => setSchedule((s) => ({ ...s, slot_duration_minutes: parseInt(v) }))}
              disabled={!canEdit}
            >
              <SelectTrigger data-testid="slot-duration-select" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SLOT_OPTIONS.map((m) => (
                  <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 mb-6">
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /> Weekly working hours</h3>
          <div className="space-y-3">
            {DAYS.map((day) => {
              const blocks = (schedule?.weekly_hours || {})[day.key] || [];
              return (
                <div key={day.key} className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3 items-start py-3 border-b border-slate-100 last:border-0" data-testid={`day-row-${day.key}`}>
                  <div className="font-medium text-slate-700 pt-1.5">{day.label}</div>
                  <div className="space-y-2">
                    {blocks.length === 0 && (
                      <div className="text-sm text-slate-400 italic">Closed</div>
                    )}
                    {blocks.map((b, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`block-${day.key}-${idx}`}>
                        <Input
                          type="time" value={b.start}
                          onChange={(e) => updateBlock(day.key, idx, "start", e.target.value)}
                          disabled={!canEdit}
                          className="w-32 h-9"
                          data-testid={`block-start-${day.key}-${idx}`}
                        />
                        <span className="text-slate-400">to</span>
                        <Input
                          type="time" value={b.end}
                          onChange={(e) => updateBlock(day.key, idx, "end", e.target.value)}
                          disabled={!canEdit}
                          className="w-32 h-9"
                          data-testid={`block-end-${day.key}-${idx}`}
                        />
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeBlock(day.key, idx)} data-testid={`remove-block-${day.key}-${idx}`}>
                            <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => addBlock(day.key)} className="h-8" data-testid={`add-block-${day.key}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add block
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><CalendarOff className="w-4 h-4 text-blue-600" /> Leave dates</h3>
          {canEdit && (
            <div className="flex items-center gap-2 mb-3">
              <Input type="date" value={newLeave} onChange={(e) => setNewLeave(e.target.value)} className="w-44 h-9" data-testid="leave-date-input" />
              <Button size="sm" onClick={addLeave} disabled={!newLeave} data-testid="add-leave-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {(schedule?.leaves || []).length === 0 && (
              <div className="text-sm text-slate-400 italic">No leaves scheduled</div>
            )}
            {(schedule?.leaves || []).map((d) => (
              <Badge key={d} variant="outline" className="px-2.5 py-1 gap-1.5 bg-amber-50 border-amber-200 text-amber-800" data-testid={`leave-${d}`}>
                {d}
                {canEdit && (
                  <button onClick={() => removeLeave(d)} className="hover:text-red-700" data-testid={`remove-leave-${d}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
